import React, { useMemo } from "react";
import { Typography } from "@/components/ui/typography";
import DashboardCardWrapper from "../common/DashboardCardWrapper";
import { CardContent } from "../ui/card";
import DashboardCardHeader from "../common/DashboardCardHeader";
import CostIconLight from "@/assets/icons/costUtilizationIconLight.svg";
import CostIconDark from "@/assets/icons/costUtilizationIconDark.svg";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { getBaseUrl } from "@/utils/urls";
import { TruncatedTextTooltip } from "../common/TextTooltip";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface CostData {
  costInpatient: number | null;
  costOutpatient: number | null;
  costED: number | null;
  costPharmacy: number | null;
  costXR: number | null;
  costLab: number | null;
  costOther: number | null;
  totalMedCost: number | null;
}

// Format currency values
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "--";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  }).format(value);
};

interface CostUtilizationCardProps {
  shouldBlink?: boolean;
}

const CostUtilizationCard: React.FC<CostUtilizationCardProps> = ({ shouldBlink = false }) => {
  const {
    cardVariant,
    isFavourite,
    toggleFavourite,
  } = useDashboardCardFavorite("TWELVE_MONTH_COST_UTILIZATION");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const { data: costData, loading, error, isErrorRed } = useFetchCard<CostData>({
    endpoint: `${getBaseUrl()}/cost-utilization`,
    memberId,
    processData: (rawData: unknown) => (rawData as { data: CostData }).data,
  });

  const display = useMemo(() => {
    if (!costData) return null;

    return {
      fields: {
        Inpatient: costData.costInpatient,
        Outpatient: costData.costOutpatient,
        ED: costData.costED,
        Pharmacy: costData.costPharmacy,
        Lab: costData.costLab,
        Imaging: costData.costXR,
        Other: costData.costOther,
      } as Record<string, number | null>,
      total: costData.totalMedCost,
    };
  }, [costData]);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <div className='w-4 h-4'>
                <img
                  src={CostIconLight}
                  alt='Cost Light'
                  className='block dark:hidden'
                />
                <img
                  src={CostIconDark}
                  alt='Cost Dark'
                  className='hidden dark:block'
                />
              </div>
            </BorderedIconWrapper>
          }
          title='12-month Cost / Utilization'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />

        <CardContent className='p-4 lg:h-[304px] h-max-content'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={8} showProfile={false} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[304px] md:h-[304px]'>
                  <Typography
                    variant='medium-14'
                    className={`${isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                      } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!costData) {
              return null;
            }

            return (
              <>
              <div className='gap-[10px] flex flex-col'>
                {display &&
                  Object.entries(display.fields).map(([key, value]) => {
                    return (
                      <div
                        key={key}
                        className='flex justify-between items-center min-w-0'>
                        <Typography
                          variant='medium-14'
                          className='text-grey-700 dark:text-gray-400 flex-shrink-0'>
                          {key}
                        </Typography>
                        <TruncatedTextTooltip
                          text={formatCurrency(value)}
                          className='min-w-0 flex-1 max-w-[60%]'>
                          <Typography
                            variant='medium-14'
                            className='text-gray-800 dark:text-white text-right truncate'>
                            {formatCurrency(value)}
                          </Typography>
                        </TruncatedTextTooltip>
                      </div>
                    );
                  })}
              </div>

              {/* Divider */}
              <div className='border-t border-dashed border-gray-300 dark:border-gray-600 my-4'></div>

              {/* Total Section */}
              <div className='bg-primary-100 dark:bg-blue-600 rounded-sm p-[8px] h-[40px]'>
                <div className='flex justify-between items-center'>
                  <Typography
                    variant='semibold-14'
                    className='text-primary-600 dark:text-primary-400'>
                    TOTAL
                  </Typography>
                  <Typography
                    variant='semibold-16'
                    className='text-primary-600 dark:text-primary-400'>
                    {display?.total != null ? formatCurrency(display.total) : "-"}
                  </Typography>
                </div>
              </div>
              </>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default CostUtilizationCard;
